import { css, CSSObject, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import toColor, { Color } from '@OnArk/utils/toColor';

// NOTE: typed against the augmented emotion Theme (not OnArkTheme) because
// @hangar31/rc-layout also augments Theme, so the two aren't interchangeable.
export const wysiwygStyles: ((props: { theme?: Theme }) => CSSObject)[] = [
  ({ theme }: { theme?: Theme }): CSSObject => ({
    blockquote: theme?.font(),
    h1: theme?.font('lg', 'bl'),
    h2: theme?.font('md', 'bl'),
    ol: theme?.font(),
    p: theme?.font(),
    ul: theme?.font(),
  }),
  ({ theme }: { theme?: Theme }): CSSObject => ({
    a: { color: toColor(theme?.church.buttonColor ?? ({} as Color)) },
    blockquote: {
      fontStyle: 'italic',
    },
    h1: {
      lineHeight: '1em',

      margin: '0.67em 0',
    },
    h2: {
      margin: '0.67em 0',
    },
    // Section images are church-uploaded at whatever size the slide export
    // produced, so constrain them here rather than trusting quill.snow.css.
    img: {
      height: 'auto',
      maxWidth: '100%',
    },
    p: {
      margin: '0.67em 0',
    },
  }),
];

export const TextStyled = styled.div(
  ({ theme }) => ({
    a: { color: toColor(theme.church.buttonColor) },
  }),
  ...wysiwygStyles
);

/**
 * Richer typography for guest-facing section HTML (check-in landings,
 * completion celebrations) — accent blockquote, labeled h2s, and
 * church-color bullets. Safe no-op on plain single-<p> seed copy.
 */
export const GuestRichCopyStyled = styled(TextStyled)(
  ({ theme }) => css`
    h2 {
      margin: 2.8rem 0 1.2rem;

      color: ${theme.colors.black};
      font-size: 1.5rem;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    blockquote {
      margin: 1.6rem 0 2rem;
      padding: 0.2rem 0 0.2rem 1.6rem;

      color: ${theme.colors.black};
      font-size: 1.85rem;
      font-style: normal;
      font-weight: 500;
      line-height: 1.35;

      border-left: 3px solid ${toColor(theme.church.buttonColor)};
    }

    ul {
      margin: 0.8rem 0 1.6rem;
      padding-left: 0;

      list-style: none;
    }

    li {
      position: relative;

      margin: 0 0 1.2rem;
      padding-left: 1.6rem;

      line-height: 1.45;
    }

    li::before {
      position: absolute;
      top: 0.55em;
      left: 0;

      width: 0.55rem;
      height: 0.55rem;

      background: ${toColor(theme.church.buttonColor)};
      border-radius: 50%;

      content: '';
    }

    em {
      color: ${theme.colors.grayDark};
    }

    p {
      margin: 1rem 0;
      line-height: 1.5;
    }
  `
);

export const TitleStyled = styled.h1(
  ({ theme }) => theme.font('lg', 'bl'),
  ({ theme }) => css`
    margin-top: 4rem;
    margin-bottom: 2.4rem;

    color: ${theme.colors.grayDark};
    text-transform: uppercase;
  `
);

export const SubTitleStyled = styled.h2(
  ({ theme }) => theme.font('md', 'bl'),
  ({ theme }) => css`
    margin: 4rem 0 2rem;

    color: ${theme.colors.grayDark};
    text-transform: uppercase;
  `
);
export const SmallTitleStyled = styled.h3(
  ({ theme }) => theme.font('sm', 'bl'),
  ({ theme }) => css`
    margin: 4rem 0 2rem;

    color: ${theme.colors.grayDark};
    text-transform: uppercase;
  `
);

export const AppTitleStyled = styled.h1(
  ({ theme }) => theme.font('lg', 'bl'),
  ({ theme }) => css`
    margin: 0 0 0 0;

    color: ${theme.colors.black};
  `
);
